/**
 * GamificationTutorial - Tutorial interativo do sistema Acácia
 */
import React, { useState } from 'react';
import { ChevronRight, ChevronLeft, X, Sprout, Droplets, Zap, Trophy, Users } from 'lucide-react';

const TUTORIAL_STEPS = [
  {
    title: 'Bem-vinda ao Sistema Acácia! 🌱',
    description: 'Sua jornada de crescimento é representada por uma planta chamada Acácia. À medida que você avança nas trilhas, sua planta cresce com você!',
    icon: Sprout,
    color: '#10B981',
    image: '🌱',
  },
  {
    title: 'Complete Ilhas para Crescer 🏝️',
    description: 'Cada ilha completa faz sua Acácia evoluir um estágio. Existem 5 ilhas e 4 estágios de crescimento. Quanto mais você aprende, mais ela floresce!',
    icon: Trophy,
    color: '#8B5CF6',
    image: '🌳',
  },
  {
    title: 'Regue sua Planta 💧',
    description: 'Você ganha regadores completando lições. Use-os para manter sua Acácia saudável! Cada rega restaura 10 pontos de saúde. Máximo: 10 regadores.',
    icon: Droplets,
    color: '#3B82F6',
    image: '💧',
  },
  {
    title: 'Turbine com Fertilizantes ⚡',
    description: 'Fertilizantes aceleram sua jornada! Cada um dá +20 pontos por 7 dias. Use até 5 ao mesmo tempo para crescer mais rápido.',
    icon: Zap,
    color: '#F59E0B',
    image: '⚡',
  },
  {
    title: 'Jardim Coletivo 👥',
    description: 'Você faz parte de um círculo com 5-7 pessoas. Juntos, vocês cultivam um jardim compartilhado! Quanto mais o círculo se engaja, mais florido fica o jardim.',
    icon: Users,
    color: '#0F6E56',
    image: '🌺',
  },
  {
    title: 'Mantenha a Consistência 📅',
    description: 'Sua Acácia perde 10 pontos por dia de inatividade. Mas não se preocupe: ela nunca morre! Volte quando puder e continue cuidando dela.',
    icon: Sprout,
    color: '#C8541A',
    image: '🍂',
  },
];

export default function GamificationTutorial({ onComplete, onSkip }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [show, setShow] = useState(true);
  
  if (!show) return null;
  
  const step = TUTORIAL_STEPS[currentStep];
  const isFirst = currentStep === 0;
  const isLast = currentStep === TUTORIAL_STEPS.length - 1;
  const Icon = step.icon;
  
  const handleNext = () => {
    if (isLast) {
      handleComplete();
    } else {
      setCurrentStep(currentStep + 1);
    }
  };
  
  const handlePrev = () => {
    if (!isFirst) {
      setCurrentStep(currentStep - 1);
    }
  };
  
  const handleComplete = () => {
    setShow(false);
    if (onComplete) onComplete();
  };
  
  const handleSkip = () => {
    setShow(false);
    if (onSkip) onSkip();
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/70 backdrop-blur-sm">
      <div className="bg-white w-full sm:max-w-2xl sm:rounded-3xl shadow-2xl overflow-hidden rounded-t-3xl max-h-[95vh] flex flex-col">
        {/* Header */}
        <div
          className="p-4 sm:p-6 text-white relative overflow-hidden flex-shrink-0"
          style={{ background: `linear-gradient(135deg, ${step.color}, ${step.color}CC)` }}
        >
          <button
            onClick={handleSkip}
            className="absolute top-3 right-3 p-2 rounded-lg hover:bg-white/20 transition-colors"
            aria-label="Fechar"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="flex items-center gap-3 mb-3 pr-8">
            <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white/20 rounded-2xl flex items-center justify-center flex-shrink-0 backdrop-blur-sm">
              <Icon className="w-6 h-6 sm:w-8 sm:h-8" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs sm:text-sm opacity-90 mb-0.5">
                Passo {currentStep + 1} de {TUTORIAL_STEPS.length}
              </div>
              <h2 className="text-lg sm:text-2xl font-bold leading-tight">{step.title}</h2>
            </div>
          </div>

          {/* Progress bar */}
          <div className="h-1 bg-white/30 rounded-full overflow-hidden">
            <div
              className="h-full bg-white rounded-full transition-all duration-500"
              style={{ width: `${((currentStep + 1) / TUTORIAL_STEPS.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-8 overflow-y-auto">
          {/* Emoji visual */}
          <div className="text-center mb-4 sm:mb-6">
            <div
              className="inline-flex items-center justify-center w-20 h-20 sm:w-32 sm:h-32 rounded-full text-4xl sm:text-6xl"
              style={{ background: `${step.color}15` }}
            >
              {step.image}
            </div>
          </div>

          {/* Description */}
          <p className="text-gray-700 text-base sm:text-lg leading-relaxed text-center mb-6 sm:mb-8">
            {step.description}
          </p>

          {/* Navigation */}
          <div className="flex items-center justify-between gap-2">
            <button
              onClick={handlePrev}
              disabled={isFirst}
              className={`
                flex items-center gap-1 sm:gap-2 px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl font-semibold transition-all text-sm sm:text-base
                ${isFirst
                  ? 'opacity-0 pointer-events-none'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }
              `}
            >
              <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
              Anterior
            </button>

            {/* Dots indicator */}
            <div className="flex items-center gap-1.5">
              {TUTORIAL_STEPS.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentStep(idx)}
                  className={`h-2 rounded-full transition-all ${idx === currentStep ? 'w-6' : 'w-2 opacity-30'}`}
                  style={{ background: step.color }}
                  aria-label={`Ir para passo ${idx + 1}`}
                />
              ))}
            </div>

            <button
              onClick={handleNext}
              className="flex items-center gap-1 sm:gap-2 px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl font-semibold text-white shadow-lg hover:shadow-xl transition-all text-sm sm:text-base"
              style={{ background: step.color }}
            >
              {isLast ? 'Começar!' : 'Próximo'}
              {!isLast && <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />}
            </button>
          </div>
        </div>

        {/* Footer */}
        {!isLast && (
          <div className="px-4 sm:px-8 pb-4 sm:pb-6 text-center flex-shrink-0">
            <button
              onClick={handleSkip}
              className="text-sm text-gray-500 hover:text-gray-700 transition-colors underline"
            >
              Pular tutorial
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
